import type { InferResponse } from '../typedClient'
import { fetchClient } from '../typedClient'

/**
 * Downloads the change-ownership worksheet for a tenant pair as an Excel
 * workbook. The endpoint is gated on gravitate_admin_access, which declares
 * X-Tenant-Name as Literal["Gravitate"], so the header is always sent.
 */
export async function fetchChangeOwnershipPlan(
  oldOwnerGoid: string,
  newOwnerGoid: string,
): Promise<Blob> {
  const { data, error } = await fetchClient.POST(
    '/crossroads/tools/change_ownership/download_plan',
    {
      params: {
        query: {
          old_owner_goid: oldOwnerGoid,
          new_owner_goid: newOwnerGoid,
        },
        header: { 'X-Tenant-Name': 'Gravitate' },
      },
      parseAs: 'blob',
    },
  )
  if (error) {
    // parseAs: 'blob' applies to error bodies too, so unwrap it to keep the
    // endpoint's detail (e.g. "No ownership changes possible") readable.
    const detail =
      error instanceof Blob ? await error.text() : JSON.stringify(error)
    throw new Error(`Failed to download change ownership plan: ${detail}`)
  }
  return data as unknown as Blob
}

export type ChangeOwnershipResults =
  InferResponse<'/crossroads/tools/change_ownership/apply_plan'>

/**
 * Applies an edited change-ownership worksheet. `forward: false` runs the
 * migration in reverse, undoing a previously applied sheet.
 */
export async function applyChangeOwnershipPlan(
  oldOwnerGoid: string,
  newOwnerGoid: string,
  file: File,
  forward = true,
): Promise<ChangeOwnershipResults> {
  const formData = new FormData()
  formData.append('file', file)

  const { data, error } = await fetchClient.POST(
    '/crossroads/tools/change_ownership/apply_plan',
    {
      params: {
        query: {
          old_owner_goid: oldOwnerGoid,
          new_owner_goid: newOwnerGoid,
          forward,
        },
        header: { 'X-Tenant-Name': 'Gravitate' },
      },
      body: formData as never,
      // The FormData has to reach fetch untouched so the browser sets the
      // multipart boundary; the default serializer would JSON-encode it.
      bodySerializer: () => formData,
    },
  )
  if (error) {
    throw new Error(
      `Failed to apply change ownership plan: ${JSON.stringify(error)}`,
    )
  }
  return data
}
